import React, { type FC } from "react";

import { Diff } from "~/components/code/diff";
import type { NodeId } from "~/models/dto";
import type { Time } from "~/tools/date-time/time";

import { Api, type EventElement } from "../../../../../../common/openapi";
import { DrawerComponentTitle, ExplorationDrawer } from "../../../../../../components/react/drawer/ExplorationDrawer";

import HealthStateValue = Api.HealthStateValue;

import { DiffHeader } from "~/components/diff-header";
import { Join } from "~/components/join";
import { JoinItem } from "~/components/join/item";

export interface EventDiffDrawerState {
	element: EventElement;
	oldCode: string;
	newCode: string;
	oldTimestamp: Time;
	newTimestamp: Time;
}

export interface EventDiffDrawerProps {
	when: undefined | EventDiffDrawerState;
}

export const EventDiffDrawer: FC<EventDiffDrawerProps> = ({ when }) => (
	<ExplorationDrawer
		data-testid="event-diff-drawer"
		displayTimestamp
		headerTitle={({ element }) => <EventDiffDrawerHeaderTitle element={element} />}
		when={when}
	>
		{({ oldCode, newCode, oldTimestamp, newTimestamp }) => (
			<Join className="h-full" direction="vertical">
				<JoinItem>
					<DiffHeader modifiedTimestamp={newTimestamp} originalTimestamp={oldTimestamp} />
				</JoinItem>
				<JoinItem>
					<Diff height="auto" language="yaml" modified={newCode} original={oldCode} />
				</JoinItem>
			</Join>
		)}
	</ExplorationDrawer>
);

const EventDiffDrawerHeaderTitle: React.FC<{
	element: EventElement;
}> = ({ element }) => {
	const icon = element._type === "EventComponent" ? element.icon : undefined;

	return (
		<DrawerComponentTitle
			component={{
				id: element.id as NodeId,
				// the reason the name is optional here is due to the EventRelation.
				// Events on relations are deprecated and effectively unused, we never executed on removing
				// though: https://www.notion.so/stackstate/Relations-lagging-behind-components-in-StackState-2ce5ac1cca6546149fa6b3758d5d7811?source=copy_link#cb6fe4424f56490ab413867792a3fe7e
				// The 'no name' should no be presented for this reason, but using a bang pattern seems harsh.
				// FIXME!
				name: element.name ?? "no name",
				identifiers: element.identifiers,
				typeName: element.typeName,
				icon,
				healthState: HealthStateValue.UNKNOWN,
			}}
			displayTypeName
			typeOfData="Last change"
		/>
	);
};
